import { useLocation } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { isPageContentLocked, selectOnboardingLock, type OnboardingLock } from '@/lib/onboardingLock'

/**
 * The nav/route lock for the current user (ClickUp 86d3uknt8).
 *
 * Reads the same profile the router's `beforeLoad` guard reads, so the menu and
 * the guard cannot disagree: an item the sidebar leaves enabled is always one
 * the guard will let through, and vice versa.
 *
 * Reactive on purpose. When the unlock lands — through the founder's own step-4
 * click or through a webhook the browser never saw — the profile query is
 * invalidated, this recomputes, and the whole shell enables without a reload.
 */
export function useOnboardingLock(): OnboardingLock {
  const { profile } = useAuth()
  const { hasFlag } = useFeatureFlags()
  return useMemo(() => selectOnboardingLock(profile, hasFlag), [profile, hasFlag])
}

/**
 * Whether THIS page's own content is dimmed and inert right now — the lock's
 * visible half, for `PageLayout`.
 *
 * The where and the whether are one question, so they are asked in one place:
 * the answer depends on the current path (the active step's own surface is the
 * page that stays live), and a shared layout should not have to know that to
 * read it off.
 */
export function useOnboardingPageLock(): boolean {
  const lock = useOnboardingLock()
  const { pathname } = useLocation()
  return isPageContentLocked(pathname, lock)
}
